import React, { JSX, ReactNode, createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'

export const CHAT_GATE_WAITING_LINE = "YouTube lines are waiting for FlightDeck's YouTube check. They show when it runs, or are skipped after 5 minutes."

export const chatGateSkippedLine = (n: number): string => n === 1
  ? '1 YouTube line was skipped because FlightDeck could not check it in time.'
  : `${n} YouTube lines were skipped because FlightDeck could not check them in time.`

export const CHAT_GATE_DISMISS = 'Dismiss'

export interface ChatGateCounts {
  waitingLong: number
  droppedUnchecked: number
}

export const EMPTY_CHAT_GATE_COUNTS: ChatGateCounts = Object.freeze({ waitingLong: 0, droppedUnchecked: 0 })

export interface ChatGateStatusSink {
  report: (reporter: object, chatId: string, counts: ChatGateCounts) => void
  retire: (reporter: object) => void
}

const SinkContext = createContext<ChatGateStatusSink | null>(null)

export const useChatGateStatusSink = (): ChatGateStatusSink | null => useContext(SinkContext)

interface Reported {
  chatId: string
  counts: ChatGateCounts
}

export const aggregateChatGateStatus = (
  live: readonly Reported[],
  retiredDropped: ReadonlyMap<string, number>
): ChatGateCounts => {
  const perChat = new Map<string, ChatGateCounts>()
  retiredDropped.forEach((dropped, chatId) => perChat.set(chatId, { waitingLong: 0, droppedUnchecked: dropped }))
  live.forEach(({ chatId, counts }) => {
    const cur = perChat.get(chatId) ?? EMPTY_CHAT_GATE_COUNTS
    perChat.set(chatId, {
      waitingLong: Math.max(cur.waitingLong, counts.waitingLong),
      droppedUnchecked: Math.max(cur.droppedUnchecked, counts.droppedUnchecked)
    })
  })
  let waitingLong = 0
  let droppedUnchecked = 0
  perChat.forEach((c) => {
    waitingLong += c.waitingLong
    droppedUnchecked += c.droppedUnchecked
  })
  return { waitingLong, droppedUnchecked }
}

export const ChatGateStatusPane = ({
  onStatus,
  children
}: {
  onStatus: (status: ChatGateCounts) => void
  children: ReactNode
}): JSX.Element => {
  const onStatusRef = useRef(onStatus)
  onStatusRef.current = onStatus
  const sink = useMemo((): ChatGateStatusSink => {
    const live = new Map<object, Reported>()
    const retired = new Map<string, number>()
    let last = ''
    const publish = (): void => {
      const next = aggregateChatGateStatus(Array.from(live.values()), retired)
      const key = `${next.waitingLong} ${next.droppedUnchecked}`
      if (key === last) return
      last = key
      onStatusRef.current(next)
    }
    return {
      report: (reporter, chatId, counts) => {
        live.set(reporter, { chatId, counts: { waitingLong: counts.waitingLong, droppedUnchecked: counts.droppedUnchecked } })
        publish()
      },
      retire: (reporter) => {
        const r = live.get(reporter)
        if (r == null) return
        live.delete(reporter)
        retired.set(r.chatId, Math.max(retired.get(r.chatId) ?? 0, r.counts.droppedUnchecked))
        publish()
      }
    }
  }, [])
  return <SinkContext.Provider value={sink}>{children}</SinkContext.Provider>
}

export const useChatGateStatusReporter = (
  sink: ChatGateStatusSink | null,
  chatId: string,
  read: () => ChatGateCounts
): void => {
  const readRef = useRef(read)
  readRef.current = read
  useEffect(() => {
    if (sink == null) return
    const reporter = {}
    let last = ''
    const tick = (): void => {
      const counts = readRef.current()
      const key = `${counts.waitingLong} ${counts.droppedUnchecked}`
      if (key === last) return
      last = key
      sink.report(reporter, chatId, counts)
    }
    tick()
    const interval = globalThis.setInterval(tick, 1000)
    return () => {
      globalThis.clearInterval(interval)
      sink.retire(reporter)
    }
  }, [sink, chatId])
}

export const chatGateStatusLinesOf = (
  status: ChatGateCounts,
  dismissedAt: number
): { waiting: string | null, skipped: string | null } => ({
  waiting: status.waitingLong > 0 ? CHAT_GATE_WAITING_LINE : null,
  skipped: status.droppedUnchecked > dismissedAt ? chatGateSkippedLine(status.droppedUnchecked) : null
})

export const ChatGateStatusLines = ({ status }: { status: ChatGateCounts }): JSX.Element | null => {
  const [dismissedAt, setDismissedAt] = useState(0)
  const lines = chatGateStatusLinesOf(status, dismissedAt)
  if (lines.waiting == null && lines.skipped == null) return null
  return (
    <div className='mt-2 flex flex-col gap-1 text-xs text-dm-text-secondary' data-testid='chat-gate-status' role='status'>
      {lines.waiting != null && <p data-testid='chat-gate-waiting'>{lines.waiting}</p>}
      {lines.skipped != null && (
        <p className='flex items-start gap-2' data-testid='chat-gate-skipped'>
          <span className='flex-1'>{lines.skipped}</span>
          <button
            type='button'
            onClick={() => setDismissedAt(status.droppedUnchecked)}
            className='shrink-0 rounded px-1 text-dm-text-accent hover:text-dm-text-primary'
          >
            {CHAT_GATE_DISMISS}
          </button>
        </p>
      )}
    </div>
  )
}
